import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import type { AssetAlternativeId } from "@shared/types/content-api";
import {
  epgMappingOf,
  epgMappingStatus,
  readEpgChannelId,
  type EpgMappingIssue,
} from "@shared/epg-mapping";
import { foyerApi } from "../../http/foyer";
import { tokens } from "../../theme";
import { SectionHead } from "./editorBits";

/**
 * The channel's EPG source id — the field that decides whether it has a
 * programme guide at all (`BE-22`).
 *
 * ## Why this is a confirm dialog and not an input with a Save button
 *
 * Because it cannot be undone. `nenda-content-api` upserts `alternativeIds` and
 * never deletes them, there is no removal route, and `(source, alternativeId)`
 * is globally unique — so one mistyped digit both sticks to this channel for
 * ever AND permanently blocks the channel the id really belongs to from ever
 * claiming it. That service is frozen and read-only to Foyer, so the trap
 * cannot be removed; the only honest response is to make the write deliberate
 * and to say plainly what it costs.
 *
 * Once set, the field is READ-ONLY. Rendering an editable box over a value the
 * backend will refuse to change is worse than rendering none: it invites the
 * exact action that produces a support case nobody can fix.
 *
 * ## Why an unmapped channel is a warning and not a validation error
 *
 * A channel with genuinely no guide is a legitimate state — an in-house
 * information loop has no schedule and never will. What is NOT legitimate is
 * not knowing: today an unmapped channel is indistinguishable from a mapped one
 * whose feed was empty, and the only trace of the difference is a log line in a
 * service with its structured reports disabled.
 */

function issueText(issue: EpgMappingIssue): string {
  switch (issue.code) {
    case "blank":
      return "An id is required.";
    case "has_whitespace":
      return "A channel id has no spaces in it. `svt 1.se` is a different string from `svt1.se` upstream, and it would match nothing while looking right.";
    case "control_characters":
      return "That value carries an invisible character — almost always a bad paste.";
    case "too_long":
      return "Longer than the catalogue's column can hold.";
    default:
      return issue.code;
  }
}

export default function ChannelEpgMapping({
  assetId,
  alternativeIds,
  onMapped,
}: {
  assetId: string;
  /** From the channel row. `undefined` means "not loaded", not "none". */
  alternativeIds: AssetAlternativeId[] | undefined;
  onMapped?: (mapping: string) => void;
}) {
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const status = epgMappingStatus({ alternativeIds });
  const current = saved ?? epgMappingOf(alternativeIds);
  const read = readEpgChannelId(value);
  const issue =
    value.trim() === "" ? null : "issue" in read ? read.issue : null;

  async function commit() {
    setBusy(true);
    setError(null);
    try {
      const answer = await foyerApi.setChannelEpgMapping(
        assetId,
        "issue" in read ? value : read.value
      );
      setSaved(answer.mapping);
      setConfirming(false);
      setValue("");
      onMapped?.(answer.mapping);
    } catch (e) {
      const err = e as {
        code?: string;
        message: string;
        holder?: { assetId: string; originalTitle: string };
        current?: string;
      };
      setError(
        err.code === "mapping_taken"
          ? `“${err.holder?.originalTitle ?? "Another asset"}” (${err.holder?.assetId}) already holds this id, and upstream has no way to release it. That channel has to be corrected first — and it cannot be.`
          : err.code === "mapping_is_permanent"
            ? `This channel is already mapped to “${err.current}”, and a mapping cannot be changed.`
            : err.code === "invalid_epg_channel_id"
              ? "That value could never match a channel in the feed."
              : err.message
      );
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box data-testid="channel-epg-mapping">
      <SectionHead
        title="Programme guide source"
        subtitle="The XMLTV channel id from the Simply TV feed. Exact match — this is the only thing that gives the channel a schedule."
        aside={
          current ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label="Mapped"
              data-testid="epg-mapping-set"
            />
          ) : status === "unmapped" ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="No guide"
              data-testid="epg-mapping-missing"
            />
          ) : null
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {current ? (
        <Box>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: 13,
              color: tokens.ink,
            }}
            data-testid="epg-mapping-value"
          >
            {current}
          </Typography>
          {/* No edit affordance, and the sentence says why rather than
              leaving somebody hunting for the button. */}
          <Typography sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}>
            Set once and permanent. The catalogue upserts these ids and has no
            way to remove one, so this cannot be changed or cleared from here —
            or from anywhere.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {status === "unmapped" && (
            <Alert
              severity="warning"
              sx={{ fontSize: 12 }}
              data-testid="epg-mapping-warning"
            >
              This channel receives no programme guide. Ingest matches on this
              id alone, so every day&rsquo;s schedule for it is dropped in
              silence — which is a legitimate state for an in-house loop, and a
              fault for anything else.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label="EPG channel id"
              placeholder="svt1.se"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              error={Boolean(issue)}
              helperText={
                issue
                  ? issueText(issue)
                  : "Copied verbatim from the feed. It cannot be corrected afterwards."
              }
              inputProps={{ "data-testid": "epg-mapping-input" }}
            />
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 0.5 }}
              disabled={busy || value.trim() === "" || Boolean(issue)}
              onClick={() => setConfirming(true)}
              data-testid="epg-mapping-save"
            >
              Set
            </Button>
          </Stack>
        </Stack>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Set this permanently?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            The channel will be mapped to{" "}
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {"issue" in read ? value.trim() : read.value}
            </Box>
            .
          </DialogContentText>
          <DialogContentText sx={{ fontSize: 13, mt: 1.5 }}>
            <b>This cannot be undone.</b> The content catalogue adds these ids
            and never removes them, and each one can belong to only one asset in
            the whole catalogue — so a wrong id stays on this channel for ever
            and blocks the channel it really belongs to from ever using it.
            Check it against the feed before continuing.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={() => void commit()}
            data-testid="epg-mapping-confirm"
          >
            Set permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
